import { useState } from 'react'
import { Link, useFetcher } from 'react-router'
import { Button, Dialog, ImageCards, Stack, Tooltip, Typography, type Action } from '../../components'
import { getAvailabilityPropsFromApi } from '../../components/Chips/AvailabilityChip'

interface StyleflowRackCardsProps {
  items: TrinityAPI.VisualizerRackType[]
  actions?: Action[]
  showLinks?: boolean
}

export function StyleflowRackCards({ items, actions = [], showLinks = false }: StyleflowRackCardsProps) {
  const fetcher = useFetcher({ key: FETCHER_KEY })
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null)

  const cardItems = items.map(item => {
    const callback = () => {
      setConfirmDelete(item.id)
      setShowConfirmation(true)
    }
    const actionsArr = deleteActionToConfirm(actions, callback)

    return {
      key: item.id,
      title: {
        text: item.fabric?.trinityNumber,
        tooltip: showLinks ? 'See in Fabric Explorer' : 'Fabric',
        link: showLinks ? `/fabric-explorer/fabrics/${item.fabric.id}` : undefined,
      },
      image: {
        uri: item.image,
        description: `${item.baseModel.description} - ${item.fabric?.description}`,
        zoom_uri: item.image,
      },
      stock: showLinks ? getAvailabilityPropsFromApi(item.fabric?.stockStatus) : undefined,
      actions: actionsArr,
      item,
    }
  })

  return (
    <>
      <ImageCards items={cardItems}>
        {item => (
          <Stack p={2} height={{ mobile: 'auto', laptop: '11rem' }} sx={{ overflowY: 'auto' }}>
            {actions && (
              <>
                <input type='hidden' name='id' value={item.id} />
                <input type='hidden' name='baseModelId' value={item.baseModel.id} />
                <input type='hidden' name='garmentType' value={item.garmentType} />
                <input type='hidden' name='fabricId' value={item.fabric?.id} />
                <input type='hidden' name='imageUri' value={item.image} />
                <input type='hidden' name='liningId' value={item.lining?.id} />
                <input type='hidden' name='buttonId' value={item.button?.id} />
                <input type='hidden' name='threadId' value={item.thread?.id} />
                <input type='hidden' name='ribknitId' value={item.ribknit?.id} />
                <input type='hidden' name='zipperId' value={item.zipper?.id} />
              </>
            )}
            <Stack
              flex={1}
              spacing={2}
              pb={2}
              sx={{ '& .MaterialRow:not(:last-child)': { borderBottom: 1, borderColor: 'grey.200' } }}
            >
              {item.fabric && (
                <MaterialRow
                  label='Fabric'
                  description={item.fabric.description}
                  link={showLinks ? `/fabric-explorer/fabrics/${item.fabric.id}` : undefined}
                />
              )}
              <MaterialRow label='Model' description={item.baseModel.description} />
              {item.lining && (
                <MaterialRow
                  label='Lining'
                  description={item.lining.trinityNumber}
                  link={showLinks ? `/fabric-explorer/fabrics/${item.lining.id}` : undefined}
                />
              )}
              {item.button && <MaterialRow label='Button' description={item.button.description} />}
              {item.thread && <MaterialRow label='Thread' description={item.thread.description} />}
              {item.ribknit && <MaterialRow label='Ribknit' description={item.ribknit.description} />}
              {item.zipper && <MaterialRow label='Zipper' description={item.zipper.description} />}
            </Stack>
          </Stack>
        )}
      </ImageCards>
      <Dialog open={showConfirmation} onClose={() => setShowConfirmation(false)}>
        <fetcher.Form method='POST'>
          <input type='hidden' name='id' value={confirmDelete ?? 0} />
          <Stack p={4} spacing={4}>
            <Typography variant='h5'>Are you sure you want to remove this item?</Typography>
            <Stack direction='row' spacing={2} justifyContent='flex-end'>
              <Button size='small' variant='outlined' onClick={() => setShowConfirmation(false)}>
                Cancel
              </Button>
              <Button
                size='small'
                type='submit'
                name='intent'
                value='delete'
                color='error'
                onClick={_ => setShowConfirmation(false)}
              >
                Remove
              </Button>
            </Stack>
          </Stack>
        </fetcher.Form>
      </Dialog>
    </>
  )
}

/** HELPERS */
const FETCHER_KEY = 'styleflowraccards'
StyleflowRackCards.FETCHER_KEY = FETCHER_KEY

interface MaterialRowProps {
  label: string
  description: string
  link?: string
}
const MaterialRow = ({ label, description, link = undefined }: MaterialRowProps) => {
  return (
    <Stack className='MaterialRow' direction='row' justifyContent='space-between'>
      <Typography variant='smallBody2'>{label}</Typography>
      <Tooltip title={description}>
        <Typography
          variant='smallBody1'
          noWrap
          width='10rem'
          align='right'
          sx={{ '& a': { color: 'primary.main', textDecoration: 'none' } }}
        >
          {link ? <Link to={link}>{description}</Link> : <>{description}</>}
        </Typography>
      </Tooltip>
    </Stack>
  )
}

const deleteActionToConfirm = (actions: Action[], callback: () => void) => {
  return structuredClone(actions).map(action => {
    if (action.type == 'delete') {
      action.callback = callback
      action.intent = undefined
      return action
    }
    return action
  })
}
